import { FC, useReducer, useMemo, useCallback } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@new/common/ui/Button/Button';
import { TimePicker } from '@new/common/ui/TimePicker/TimePicker';
import { format } from 'date-fns';
import { DatePickerField } from '../shared/DatePickerField';
import { DayButton } from './DayButton';
import { useCreateBulkSlotsMutation } from '@teachur/http-client';
import {
  DAYS_OF_WEEK_WITH_VALUES,
  DEFAULT_START_TIME,
  DEFAULT_END_TIME,
  DEFAULT_WEEKDAYS,
  DATE_FORMAT_SHORT,
} from '../../lib/constants';
import { generateBulkSlots } from '../../lib/helpers';
import Modal from '@new/common/ui/Modal/Modal';

interface DateRangePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormState {
  startDate: Date | undefined;
  endDate: Date | undefined;
  startTime: string;
  endTime: string;
  selectedDays: number[];
  startDateOpen: boolean;
  endDateOpen: boolean;
}

type FormAction =
  | { type: 'SET_START_DATE'; payload: Date | undefined }
  | { type: 'SET_END_DATE'; payload: Date | undefined }
  | { type: 'SET_START_TIME'; payload: string }
  | { type: 'SET_END_TIME'; payload: string }
  | { type: 'TOGGLE_DAY'; payload: number }
  | { type: 'SET_START_DATE_OPEN'; payload: boolean }
  | { type: 'SET_END_DATE_OPEN'; payload: boolean }
  | { type: 'RESET_FORM' };

const initialState: FormState = {
  startDate: undefined,
  endDate: undefined,
  startTime: DEFAULT_START_TIME,
  endTime: DEFAULT_END_TIME,
  selectedDays: DEFAULT_WEEKDAYS,
  startDateOpen: false,
  endDateOpen: false,
};

const formReducer = (state: FormState, action: FormAction): FormState => {
  switch (action.type) {
    case 'SET_START_DATE':
      return { ...state, startDate: action.payload };
    case 'SET_END_DATE':
      return { ...state, endDate: action.payload };
    case 'SET_START_TIME':
      return { ...state, startTime: action.payload };
    case 'SET_END_TIME':
      return { ...state, endTime: action.payload };
    case 'TOGGLE_DAY':
      return {
        ...state,
        selectedDays: state.selectedDays.includes(action.payload)
          ? state.selectedDays.filter((d) => d !== action.payload)
          : [...state.selectedDays, action.payload],
      };
    case 'SET_START_DATE_OPEN':
      return { ...state, startDateOpen: action.payload };
    case 'SET_END_DATE_OPEN':
      return { ...state, endDateOpen: action.payload };
    case 'RESET_FORM':
      return initialState;
    default:
      return state;
  }
};

export const DateRangePickerModal: FC<DateRangePickerModalProps> = ({ isOpen, onClose }) => {
  const [state, dispatch] = useReducer(formReducer, initialState);
  const { startDate, endDate, startTime, endTime, selectedDays, startDateOpen, endDateOpen } =
    state;

  const createBulkSlotsMutation = useCreateBulkSlotsMutation();

  const toggleDay = useCallback((day: number) => {
    dispatch({ type: 'TOGGLE_DAY', payload: day });
  }, []);

  const slots = useMemo(() => {
    if (!startDate || !endDate || !startTime || !endTime || !selectedDays.length) {
      return [];
    }
    return generateBulkSlots(startDate, endDate, startTime, endTime, selectedDays);
  }, [startDate, endDate, startTime, endTime, selectedDays]);

  const resetForm = useCallback(() => {
    dispatch({ type: 'RESET_FORM' });
  }, []);

  const handleCreate = useCallback(() => {
    if (slots.length) {
      createBulkSlotsMutation.mutate(
        { userAvailabilitySlots: slots },
        {
          onSuccess: () => {
            onClose();
            resetForm();
          },
        }
      );
    }
  }, [slots, createBulkSlotsMutation, onClose, resetForm]);

  const today = useMemo(() => new Date(), []);

  const showPreview = !!startDate && !!endDate && !!selectedDays.length;
  const canCreateSlots =
    !!startDate &&
    !!endDate &&
    !!startTime &&
    !!endTime &&
    !!selectedDays.length &&
    !createBulkSlotsMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create availability range">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-4">
            <DatePickerField
              label="Start Date"
              value={startDate}
              onChange={(date) => dispatch({ type: 'SET_START_DATE', payload: date })}
              isOpen={startDateOpen}
              onOpenChange={(open) => dispatch({ type: 'SET_START_DATE_OPEN', payload: open })}
              disabled={(date) => date < today}
            />
            <DatePickerField
              label="End Date"
              value={endDate}
              onChange={(date) => dispatch({ type: 'SET_END_DATE', payload: date })}
              isOpen={endDateOpen}
              onOpenChange={(open) => dispatch({ type: 'SET_END_DATE_OPEN', payload: open })}
              disabled={(date) => date < (startDate || today)}
            />
          </div>

          <div className="flex justify-between gap-4">
            <div className="flex-1 flex flex-col gap-2">
              <label className="block text-sm font-medium text-(--neutral-white-NW300)">
                Start Time
              </label>
              <TimePicker
                value={startTime}
                onChange={(time) => dispatch({ type: 'SET_START_TIME', payload: time })}
                className="w-full"
              />
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <label className="block text-sm font-medium text-(--neutral-white-NW300)">
                End Time
              </label>
              <TimePicker
                value={endTime}
                onChange={(time) => dispatch({ type: 'SET_END_TIME', payload: time })}
                className="w-full"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="block text-sm font-medium text-neutral-700">Days of the week</label>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK_WITH_VALUES.map((day) => (
                <DayButton
                  key={day.value}
                  label={day.label}
                  value={day.value}
                  isSelected={selectedDays.includes(day.value)}
                  onClick={toggleDay}
                />
              ))}
            </div>
          </div>

          {showPreview && (
            <div className="p-4 bg-(--primary-O200) rounded-lg">
              <p className="[font-size:var(--fs-l4)] text-(--primary-O900) font-medium m-0!">
                This will create hourly slots from {startTime} to {endTime} on selected days between{' '}
                {format(startDate, DATE_FORMAT_SHORT)} and {format(endDate, DATE_FORMAT_SHORT)}
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button onClick={onClose} variant="neutral" className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="primary"
            disabled={!canCreateSlots}
            className="flex-1"
          >
            <CalendarIcon className="w-4 h-4 mr-2" />
            {createBulkSlotsMutation.isPending ? 'Creating...' : 'Create Slots'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
